import { useQuery } from '@tanstack/react-query';

import { createOrderV2Client } from '../api/client';
import { orderV2QueryKeys } from '../api/queryKeys';
import { SkillListItem } from '../api/types';

const useSkills = (billingAccountId: number | null, skillSearch: string) => {
  const enabled = billingAccountId != null;
  const query = useQuery<SkillListItem[]>({
    queryKey: enabled
      ? orderV2QueryKeys.skills(billingAccountId, skillSearch)
      : ['orders_v2', 'skills', 'disabled'],
    queryFn: () =>
      createOrderV2Client
        .listSkills(billingAccountId as number, skillSearch || undefined)
        .then((result) => result || []),
    enabled,
  });

  return {
    skills: query.data ?? [],
    skillsLoading: enabled && query.isFetching,
    // True once the account-scoped skills query has resolved successfully
    // at least once — *including* a settled empty list. The missing-skill
    // guards must key off this rather than `skills.length > 0`, otherwise
    // an empty `/billing_accounts/:id/skills` response is indistinguishable
    // from an unsettled fetch and a template-loaded `skill_id` slips
    // through as a blank/orphaned card. Stays false while disabled (no
    // account) or during the first in-flight fetch so we never false-flag
    // during the post-template-apply fan-out.
    skillsSettled: enabled && query.isSuccess && !query.isError,
    skillsError: query.error
      ? (query.error as Error).message || 'Failed to load skills'
      : null,
  };
};

export default useSkills;
